import React, { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { success, error as toastError } from '@innovyze/stylovyze';
import {
  CONTEXT_MAX_UPLOAD_BYTES,
  CONTEXT_MAX_UPLOAD_MB,
  CONTEXT_RAG_THRESHOLD_KB,
  uploadContextDocument,
} from '@/services/knowledgeApi';

const ALLOWED_EXTENSIONS = ['.txt', '.pdf', '.docx'] as const;

interface SourceUploadProps {
  scope: 'user' | 'tenant';
  onUploaded: () => void;
}

export const SourceUpload = ({ scope, onUploaded }: SourceUploadProps) => {
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const [sourcePrompt, setSourcePrompt] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File | null) => {
    if (!file || uploading) return;
    const lowerName = file.name.toLowerCase();
    if (!ALLOWED_EXTENSIONS.some(ext => lowerName.endsWith(ext))) {
      dispatch(toastError('Supported formats: .txt, .pdf, and .docx'));
      return;
    }
    if (file.size > CONTEXT_MAX_UPLOAD_BYTES) {
      dispatch(
        toastError(`File must be ${CONTEXT_MAX_UPLOAD_MB} MB or smaller`),
      );
      return;
    }

    setUploading(true);
    try {
      const created = await uploadContextDocument({
        file,
        scope,
        sourcePrompt,
      });
      const indexing =
        created.ingestion_status === 'processing'
          ? ' Indexing passages — you can attach it once status is Ready.'
          : '';
      dispatch(success(`Uploaded "${created.name}".${indexing}`));
      setSourcePrompt('');
      onUploaded();
    } catch (e) {
      dispatch(
        toastError(e instanceof Error ? e.message : 'Failed to upload source'),
      );
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-[#768D95] leading-relaxed">
        .txt, .pdf, or .docx up to {CONTEXT_MAX_UPLOAD_MB} MB (text-based PDFs
        only — scanned pages need OCR). Files under {CONTEXT_RAG_THRESHOLD_KB}{' '}
        KB are sent in full; larger files are chunked for search (Processing
        until ready).
      </p>

      <label className="block text-xs font-medium text-[#768D95]">
        Prompt used (optional)
        <input
          value={sourcePrompt}
          onChange={e => setSourcePrompt(e.target.value)}
          placeholder="Why are you adding this source?"
          className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-[#252F33] outline-none focus:border-[#007CA0]"
        />
      </label>

      <input
        ref={inputRef}
        type="file"
        accept=".txt,.pdf,.docx,text/plain,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        onChange={e => handleFile(e.target.files?.[0] ?? null)}
      />

      <button
        type="button"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        className="inline-flex items-center gap-2 rounded-md bg-[#007CA0] text-white text-sm font-medium px-3 py-2 hover:bg-[#006688] disabled:opacity-60">
        <Upload className="h-4 w-4" />
        {uploading ? 'Uploading…' : 'Choose file'}
      </button>
    </div>
  );
};
